import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import api, { formatApiError } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Sparkles, Send, Copy, Check, Lock, Info, AlertTriangle, Paperclip,
  FileText, Loader2, BookMarked, Download, FileDown, Plus,
} from "lucide-react";
import AIQuotaMeter from "@/components/AIQuotaMeter";
import {
  fingerprintPayload, beginSubmit, resolveActiveJobForRestore,
  persistJobId, clearActiveJob, defaultUuidGen, isTerminalJobDoc,
  getInternalTestStatus,
} from "@/lib/aiJobsIdempotency";
import {
  buildStreamingAssistPayload,
  canSubmitAssist,
  assistHttpError,
  readFileAsAttachment,
  resolveAssistContext,
  validateAttachmentSelection,
} from "@/lib/aiAttachments";

/* ═══════════════════════════════════════════════════════════════
   Colega IA · /app/asistente-ia
   Asistente jurídico experto en derecho chileno.
   Solo abogados con suscripción activa.
   ═══════════════════════════════════════════════════════════════ */

// Fase 1 · Catálogo canónico de materias jurídicas (id → etiqueta visible)
const LEGAL_AREAS = [
  ["constitutional", "Constitucional"], ["civil", "Civil"],
  ["civil_procedure", "Procesal civil"], ["criminal", "Penal"],
  ["criminal_procedure", "Procesal penal"], ["labor", "Laboral"],
  ["social_security", "Previsional"], ["family", "Familia"],
  ["commercial_corporate", "Comercial y societario"], ["insolvency", "Concursal"],
  ["administrative", "Administrativo"], ["municipal", "Municipal"],
  ["tax_customs", "Tributario y aduanero"], ["environmental", "Ambiental"],
  ["consumer", "Consumo"], ["local_police", "Policía Local"],
  ["competition", "Libre competencia"],
  ["intellectual_property", "Propiedad intelectual e industrial"],
  ["data_technology", "Protección de datos y tecnología"],
  ["immigration", "Migratorio"],
  ["real_estate_urban", "Inmobiliario y urbanístico"],
  ["health", "Sanitario"], ["mining", "Minero"], ["water", "Aguas"],
  ["electoral", "Electoral"], ["public_procurement", "Contratación pública"],
  ["financial_securities", "Financiero y mercado de valores"],
  ["military_justice", "Justicia militar"], ["human_rights", "Derechos humanos"],
  ["international_law", "Derecho internacional público y privado"],
  ["mediation_adr", "Mediación y resolución de conflictos"],
  ["other", "Otra materia jurídica"],
];
const LEGAL_AREA_LABEL = Object.fromEntries(LEGAL_AREAS);

export default function AsistenteIA() {
  const { user } = useAuth();
  const { config } = useConfig();
  // Fase 1 · Selector de materias. Modo automático por defecto (multiselect deshabilitado).
  const [legalAreaMode, setLegalAreaMode] = useState("automatic"); // 'automatic' | 'manual'
  const [primaryArea, setPrimaryArea] = useState("");   // '' = auto
  const [relatedAreas, setRelatedAreas] = useState([]); // máx 2
  const [customLegalArea, setCustomLegalArea] = useState(""); // sólo cuando primary === 'other'
  const [areaSearch, setAreaSearch] = useState("");
  const [areaOpen, setAreaOpen] = useState(false);
  const [context, setContext] = useState("");
  const [output, setOutput] = useState("");
  const [partialEvidence, setPartialEvidence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");        // mensaje progresivo mientras corre el stream
  const [heartbeats, setHeartbeats] = useState(0); // segundos acumulados esperando a la IA
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'docx' | null
  const [quotaTick, setQuotaTick] = useState(0);    // fuerza refetch del medidor tras cada consulta
  const [internalTestStatus, setInternalTestStatus] = useState(null);
  const [attachments, setAttachments] = useState([]); // [{ file, name, mime, b64, sizeKB }]
  const [attachErr, setAttachErr] = useState("");
  const [attachmentsEncoding, setAttachmentsEncoding] = useState(false);
  const [draftingClarificationAttempted, setDraftingClarificationAttempted] = useState(false);
  const [draftingClarification, setDraftingClarification] = useState(null);
  const [draftingClarificationResponse, setDraftingClarificationResponse] = useState("");
  const [draftingSelectedBranch, setDraftingSelectedBranch] = useState("");
  const [consultationSession, setConsultationSession] = useState(null);
  const attachmentInputRef = useRef(null);

  const isLawyer = user?.role === "lawyer";
  const isPremium = user?.plan && user.plan !== "free";
  const hasAccess = isLawyer && isPremium;

  useEffect(() => {
    let active = true;
    if (!user?.id) {
      setInternalTestStatus(null);
      return () => { active = false; };
    }
    api.get("/ai/internal-test/status")
      .then(({ data }) => {
        if (active) setInternalTestStatus(getInternalTestStatus(data));
      })
      .catch(() => {
        if (active) setInternalTestStatus(null);
      });
    return () => { active = false; };
  }, [user?.id, quotaTick]);

  useEffect(() => {
    if (user) {
      api.post("/ai/track", { event: "ai_tool_opened" }).catch(() => {});
    }
  }, [user]);

  const addArea = (id) => {
    if (legalAreaMode !== "manual") return;
    if (!id || !LEGAL_AREA_LABEL[id]) return;
    if (!primaryArea) { setPrimaryArea(id); setAreaSearch(""); return; }
    if (primaryArea === id) return;
    if (relatedAreas.includes(id)) return;
    if (relatedAreas.length >= 2) return;
    setRelatedAreas([...relatedAreas, id]);
    setAreaSearch("");
  };
  const removeArea = (id) => {
    if (legalAreaMode !== "manual") return;
    if (primaryArea === id) {
      // Promover primera relacionada a principal
      const [next, ...rest] = relatedAreas;
      setPrimaryArea(next || "");
      setRelatedAreas(rest);
      if (next !== "other") setCustomLegalArea("");
    } else {
      setRelatedAreas(relatedAreas.filter((r) => r !== id));
    }
  };
  const switchLegalAreaMode = (mode) => {
    if (mode === legalAreaMode) return;
    setLegalAreaMode(mode);
    if (mode === "automatic") {
      // Limpiar materia principal, relacionadas y custom al volver a automático.
      setPrimaryArea("");
      setRelatedAreas([]);
      setCustomLegalArea("");
      setAreaSearch("");
      setAreaOpen(false);
    }
  };
  const filteredAreas = LEGAL_AREAS.filter(([id, label]) => {
    if (id === primaryArea || relatedAreas.includes(id)) return false;
    if (!areaSearch.trim()) return true;
    const q = areaSearch.toLowerCase();
    return label.toLowerCase().includes(q) || id.includes(q);
  });

  // ═════════════════════════════════════════════════════════════
  // Polling y recuperación de jobs V2 durables.
  // ═════════════════════════════════════════════════════════════
  const pollIntervalRef = useRef(null);

  const stopJobPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const renderJobResult = (jobDoc) => {
    return jobDoc?.partial_result?.answer || jobDoc?.result?.answer || "";
  };

  const applyAssistantResult = (answer, stats = {}) => {
    if (stats.drafting_clarification_required) {
      setOutput("");
      setDraftingClarification({
        kind: stats.drafting_clarification_kind || "missing_facts",
        prompt: stats.drafting_clarification_prompt || answer,
        options: stats.drafting_clarification_options || [],
        missing: stats.drafting_minimum_facts_missing || [],
      });
      setDraftingClarificationResponse("");
      setDraftingSelectedBranch(stats.drafting_branch || "");
      setDraftingClarificationAttempted(false);
      return;
    }
    setOutput(answer || "");
    setDraftingClarification(null);
    setDraftingClarificationResponse("");
    setDraftingSelectedBranch("");
    setDraftingClarificationAttempted(false);
  };

  const getJobEvidence = (jobDoc) => {
    const result = jobDoc?.result || {};
    const partial = jobDoc?.partial_result || {};
    const evidence = partial.evidence || result.evidence || {};
    return {
      normativa: evidence.normativa || partial.normativa || result.normativa || [],
      jurisprudencia: evidence.verified_jurisprudence || partial.jurisprudencia || result.jurisprudencia || [],
    };
  };

  const applyJobDoc = (jobDoc) => {
    setStatus(jobDoc.progress_message || (jobDoc.phase ? "Fase " + jobDoc.phase : ""));
    const evidence = getJobEvidence(jobDoc);
    const hasEvidence = evidence.normativa.length > 0 || evidence.jurisprudencia.length > 0;
    setPartialEvidence(
      (jobDoc?.stage_c_state?.status === "recoverable_timeout" || hasEvidence)
        ? { ...evidence, recoverable: jobDoc?.stage_c_state?.status === "recoverable_timeout" }
        : null,
    );
    if (isTerminalJobDoc(jobDoc)) {
      if (jobDoc.status === "failed") {
        setError("La investigación falló: " + (jobDoc.error_code || "desconocido"));
      } else {
        const stats = jobDoc?.engine_stats || jobDoc?.result?.stats || {};
        applyAssistantResult(renderJobResult(jobDoc), stats);
      }
      setLoading(false);
      stopJobPolling();
      clearActiveJob(localStorage);
      setQuotaTick((t) => t + 1);
    } else if (jobDoc.status === "stale") {
      setError("La investigación quedó pendiente. Se reintentará automáticamente.");
    }
  };

  const startJobPolling = (jobId) => {
    stopJobPolling();
    setLoading(true);
    const tick = async () => {
      try {
        const { data } = await api.get(`/ai/research-jobs/${jobId}`);
        applyJobDoc(data);
      } catch (e) {
        // 404 → job desapareció; limpia sin hacer ruido.
        if (e?.response?.status === 404) {
          clearActiveJob(localStorage);
          stopJobPolling(); setLoading(false);
        }
      }
    };
    tick();
    pollIntervalRef.current = setInterval(tick, 3000);
  };

  // Recuperación tras refresh: SÓLO si la entrada activa pertenece al
  // usuario autenticado actual. Si cambió la cuenta, la entrada se limpia
  // sin consultar el job (nunca revelamos jobs de otro user).
  useEffect(() => {
    if (!user?.id) return () => stopJobPolling();
    const restored = resolveActiveJobForRestore(user.id, localStorage);
    if (restored && restored.job_id) startJobPolling(restored.job_id);
    return () => stopJobPolling();
  }, [user?.id]);

  const startNewConsultation = () => {
    if (loading) return;
    const previousSessionId = consultationSession?.id;
    stopJobPolling();
    clearActiveJob(localStorage);
    setLegalAreaMode("automatic");
    setPrimaryArea("");
    setRelatedAreas([]);
    setCustomLegalArea("");
    setAreaSearch("");
    setAreaOpen(false);
    setContext("");
    setOutput("");
    setPartialEvidence(null);
    setStatus("");
    setHeartbeats(0);
    setError("");
    setCopied(false);
    setExporting(null);
    setAttachments([]);
    setAttachErr("");
    setAttachmentsEncoding(false);
    setDraftingClarificationAttempted(false);
    setDraftingClarification(null);
    setDraftingClarificationResponse("");
    setDraftingSelectedBranch("");
    setConsultationSession(null);
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    setQuotaTick((tick) => tick + 1);
    if (previousSessionId) {
      api.post(`/ai/consultation-sessions/${previousSessionId}/close`).catch(() => {});
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hasAccess) return;
    if (loading) return;  // impide doble envío
    if (draftingClarification && draftingClarificationResponse.trim().length < 2) {
      setError("Selecciona una alternativa o escribe tu aclaración para continuar.");
      return;
    }
    if (!canSubmitAssist(context, attachments)) {
      setError("Escribe al menos 10 caracteres o adjunta un archivo válido.");
      return;
    }
    if (attachmentsEncoding) {
      setAttachErr("Espera a que termine la codificación de los archivos.");
      return;
    }
    const submissionContext = resolveAssistContext(context, attachments);
    const isClarificationSubmission = Boolean(draftingClarification);
    const clarificationResponse = isClarificationSubmission
      ? draftingClarificationResponse.trim()
      : "";
    const clarificationBranch = isClarificationSubmission
      ? draftingSelectedBranch
      : "";
    const clarificationAttempted = (
      isClarificationSubmission || draftingClarificationAttempted
    );
    const continuationType = isClarificationSubmission
      ? "clarification"
      : consultationSession?.id ? "complement" : "initial";
    setLoading(true); setError(""); setOutput(""); setPartialEvidence(null);
    setStatus("Enviando consulta..."); setHeartbeats(0);

    const isManual = legalAreaMode === "manual";
    const jobsEnabled = !!(config && config.legal_research_jobs_enabled);

    // ═════════════════════════════════════════════════════════════
    // FLUJO JOBS DURABLES (V2 jobs) — desacoplado del navegador
    // ═════════════════════════════════════════════════════════════
    if (jobsEnabled) {
      try {
        await api.post("/ai/track", { event: "ai_prompt_submitted" }).catch(() => {});
        // Idempotency por envío deliberado, con scoping por usuario y
        // reintento del mismo POST si el payload no cambió.
        const currentPayload = {
          context: submissionContext,
          legal_area_mode: legalAreaMode,
          primary_legal_area: isManual ? (primaryArea || null) : null,
          related_legal_areas: isManual ? relatedAreas : [],
          custom_legal_area: (isManual && primaryArea === "other")
            ? (customLegalArea.trim() || null) : null,
          drafting_clarification_attempted: clarificationAttempted,
          drafting_clarification_response: clarificationResponse || null,
          drafting_selected_branch: clarificationBranch || null,
          session_id: consultationSession?.id || null,
          continuation_type: continuationType,
        };
        const fp = fingerprintPayload(currentPayload);
        const entry = beginSubmit(user.id, fp, localStorage, defaultUuidGen);
        const { data } = await api.post("/ai/research-jobs", {
          ...currentPayload,
          idempotency_key: entry.idempotency_key,
        });
        persistJobId(user.id, data.job_id, localStorage);
        setStatus("Investigación en curso…");
        startJobPolling(data.job_id);
      } catch (e) {
        setLoading(false);
        setError(e?.response?.data?.detail || e.message || "No se pudo crear la investigación.");
      }
      return;
    }

    // ═════════════════════════════════════════════════════════════
    // FLUJO STREAMING LEGACY (V1) — se conserva intacto para rollback
    // ═════════════════════════════════════════════════════════════
    try {
      await api.post("/ai/track", { event: "ai_prompt_submitted" }).catch(() => {});
      const token = localStorage.getItem("lexlink_token");
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiBase}/api/ai/assist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/x-ndjson",
        },
        body: JSON.stringify(buildStreamingAssistPayload({
          context: submissionContext,
          attachments,
          legalAreaMode,
          primaryArea,
          relatedAreas,
          customLegalArea,
          draftingClarificationAttempted: clarificationAttempted,
          draftingClarificationResponse: clarificationResponse,
          draftingSelectedBranch: clarificationBranch,
          sessionId: consultationSession?.id || "",
          continuationType,
        })),
      });

      if (!resp.ok) {
        // 429 / 403 / 402 / etc → cuerpo JSON con detail
        let detail = "";
        if (resp.status !== 413) {
          try {
            const j = await resp.json();
            detail = formatApiError(j.detail);
          } catch { /* noop */ }
        }
        setError(assistHttpError(resp.status, detail));
        if (resp.status === 429) setQuotaTick((t) => t + 1);
        return;
      }

      // Lectura del stream NDJSON — un frame por línea.
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let doneFrame = null;
      let errFrame = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Consumimos líneas completas
        let idx;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          const raw = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (!raw) continue;
          let frame;
          try { frame = JSON.parse(raw); } catch { continue; }
          if (frame.type === "status") {
            setStatus(frame.message || "Procesando...");
          } else if (frame.type === "heartbeat") {
            setHeartbeats(frame.elapsed_s || 0);
            setStatus((s) => s || "Redactando con Colega IA...");
          } else if (frame.type === "done") {
            doneFrame = frame;
          } else if (frame.type === "error") {
            errFrame = frame;
          }
        }
      }

      if (errFrame) {
        setError(errFrame.detail || "Error generando la respuesta.");
      } else if (doneFrame) {
        if (doneFrame.consultation_session?.id) {
          setConsultationSession(doneFrame.consultation_session);
        }
        applyAssistantResult(doneFrame.output || "", doneFrame.engine_stats || {});
        setQuotaTick((t) => t + 1);
      } else {
        setError("La respuesta se interrumpió antes de completarse. Intenta nuevamente.");
      }
    } catch (err) {
      setError(err.message || "Error de red al consultar Colega IA.");
    } finally {
      setLoading(false);
      setStatus("");
      setHeartbeats(0);
    }
  };

  const copyOutput = async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const exportFile = async (kind) => {
    if (!output || exporting) return;
    setExporting(kind);
    try {
      const token = localStorage.getItem("lexlink_token");
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiBase}/api/ai/export/${kind}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ output }),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 15);
      a.download = `colega-ia-investigacion-${ts}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      api.post("/ai/track", { event: "ai_output_generated", metadata: { export: kind } }).catch(() => {});
    } catch (err) {
      setError(`No fue posible exportar: ${err.message}`);
    } finally { setExporting(null); }
  };

  if (!isLawyer) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <div className="panel p-8 text-center" data-testid="ai-not-lawyer">
          <Lock size={32} className="mx-auto text-muted-foreground"/>
          <h2 className="serif text-3xl mt-4">Sección exclusiva para abogados</h2>
          <p className="text-sm text-muted-foreground mt-3">
            <strong>Colega IA</strong> está disponible únicamente para cuentas con rol <strong>Abogado/a</strong> y suscripción activa.
          </p>
          <Link to="/app" className="btn-ghost mt-6 inline-flex">Volver al panel</Link>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return <UpgradeScreen/>;
  }

  return (
    <div className="container-app py-10 max-w-6xl colega-ia-font" data-testid="asistente-ia-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/30 to-emerald-400/20 border border-cyan-400/40 flex items-center justify-center">
          <Sparkles size={22} className="text-cyan-300"/>
        </div>
        <div className="flex-1">
          <div className="label-eyebrow text-cyan-300">Asistente jurídico premium</div>
          <h1 className="serif text-4xl mt-1">Colega IA</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Asistente jurídico experto en <strong className="text-foreground">derecho chileno vigente</strong>. Cita siempre las fuentes
            (leyes, códigos y jurisprudencia) en formato académico y las adjunta como notas al pie de la respuesta.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={startNewConsultation}
            disabled={loading}
            className="btn-ghost text-xs py-2 inline-flex items-center gap-1.5 shrink-0 border-cyan-400/40 text-cyan-100 hover:bg-cyan-400/10 disabled:opacity-50"
            data-testid="ai-new-consultation"
          >
            <Plus size={13}/> Nueva consulta
          </button>
          <Link
            to="/app/asistente-ia/mis-consultas"
            className="btn-ghost text-xs py-2 inline-flex items-center gap-1.5 shrink-0 border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/10"
            data-testid="ai-nav-mis-consultas"
          >
            <BookMarked size={13}/> Mis consultas
          </Link>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-md border border-amber-400/30 bg-amber-400/5 text-xs text-amber-100 flex items-start gap-2" data-testid="ai-warning">
        <AlertTriangle size={14} className="text-amber-300 shrink-0 mt-0.5"/>
        <div>
          <strong>Colega IA entrega apoyo profesional, no reemplaza el criterio del abogado.</strong> Todo contenido debe ser
          revisado, validado y adaptado antes de su uso judicial o extrajudicial. Evita ingresar datos personales sensibles
          si no son estrictamente necesarios.
        </div>
      </div>

      <div className="mt-4">
        <AIQuotaMeter refreshKey={quotaTick} onPackPurchased={() => setQuotaTick((t) => t + 1)} />
        {internalTestStatus && (
          <div className="mt-2 text-xs text-muted-foreground" data-testid="ai-internal-test-status">
            <span className="font-medium text-cyan-200">Modo de prueba interno</span>
            <span> · {internalTestStatus.remaining}/{internalTestStatus.limit} consultas disponibles</span>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="mt-6 grid lg:grid-cols-5 gap-6">
        <aside className="lg:col-span-2 panel p-5 space-y-3" data-testid="ai-legal-area-selector">
          <div className="label-eyebrow">Materia jurídica</div>

          {/* Toggle Detección automática / Elegir materias */}
          <div
            className="inline-flex rounded-md border border-white/10 bg-white/5 p-0.5 text-[11px]"
            data-testid="ai-legal-area-mode"
            role="tablist"
            aria-label="Modo de selección de materia jurídica"
          >
            <button
              type="button"
              role="tab"
              aria-selected={legalAreaMode === "automatic"}
              onClick={() => switchLegalAreaMode("automatic")}
              className={`px-3 py-1.5 rounded-md transition ${
                legalAreaMode === "automatic"
                  ? "bg-cyan-400/20 text-cyan-100 border border-cyan-400/40"
                  : "text-muted-foreground hover:text-white/90"
              }`}
              data-testid="ai-legal-area-mode-automatic"
            >
              Detección automática
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={legalAreaMode === "manual"}
              onClick={() => switchLegalAreaMode("manual")}
              className={`px-3 py-1.5 rounded-md transition ${
                legalAreaMode === "manual"
                  ? "bg-cyan-400/20 text-cyan-100 border border-cyan-400/40"
                  : "text-muted-foreground hover:text-white/90"
              }`}
              data-testid="ai-legal-area-mode-manual"
            >
              Elegir materias
            </button>
          </div>

          <div className="text-[11px] text-muted-foreground">
            {legalAreaMode === "automatic"
              ? "Colega IA detectará automáticamente la materia principal a partir del contexto."
              : "Selecciona una materia principal y hasta 2 materias relacionadas."}
          </div>

          {/* Chips y buscador — visibles siempre; deshabilitados y atenuados en modo automático */}
          <div
            className={`space-y-3 ${legalAreaMode === "automatic" ? "opacity-50 pointer-events-none select-none" : ""}`}
            aria-disabled={legalAreaMode === "automatic"}
            data-testid="ai-legal-area-multiselect"
          >
            <div className="flex flex-wrap gap-1.5 min-h-[26px]" data-testid="ai-legal-area-chips">
              {primaryArea && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-cyan-400/15 border border-cyan-400/40 text-cyan-100"
                      data-testid={`ai-area-chip-primary-${primaryArea}`}>
                  <span className="text-[10px] opacity-70">principal</span>
                  {LEGAL_AREA_LABEL[primaryArea]}
                  <button type="button" onClick={() => removeArea(primaryArea)}
                    className="ml-1 hover:text-red-300" aria-label="Quitar materia principal"
                    disabled={legalAreaMode !== "manual"}
                    data-testid={`ai-area-remove-${primaryArea}`}>×</button>
                </span>
              )}
              {relatedAreas.map((id) => (
                <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-white/5 border border-white/10 text-white/90"
                      data-testid={`ai-area-chip-related-${id}`}>
                  {LEGAL_AREA_LABEL[id]}
                  <button type="button" onClick={() => removeArea(id)}
                    className="ml-1 hover:text-red-300" aria-label={`Quitar materia ${LEGAL_AREA_LABEL[id]}`}
                    disabled={legalAreaMode !== "manual"}
                    data-testid={`ai-area-remove-${id}`}>×</button>
                </span>
              ))}
              {!primaryArea && relatedAreas.length === 0 && (
                <span className="text-xs text-muted-foreground/80 italic">
                  {legalAreaMode === "automatic" ? "Detección automática activa" : "Sin materia seleccionada"}
                </span>
              )}
            </div>

            {(primaryArea ? relatedAreas.length < 2 : true) && (
              <div className="relative">
                <input
                  type="text" className="field w-full text-sm"
                  placeholder={primaryArea ? "Agregar materia relacionada (máx. 2)…" : "Buscar y seleccionar materia principal…"}
                  value={areaSearch}
                  onChange={(e) => { setAreaSearch(e.target.value); setAreaOpen(true); }}
                  onFocus={() => setAreaOpen(true)}
                  onBlur={() => setTimeout(() => setAreaOpen(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredAreas.length > 0) {
                      e.preventDefault(); addArea(filteredAreas[0][0]);
                    } else if (e.key === "Escape") { setAreaOpen(false); }
                  }}
                  disabled={legalAreaMode !== "manual"}
                  data-testid="ai-legal-area-search"
                />
                {areaOpen && legalAreaMode === "manual" && filteredAreas.length > 0 && (
                  <ul className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-md border border-white/10 bg-neutral-900 shadow-lg text-sm"
                      data-testid="ai-legal-area-list">
                    {filteredAreas.slice(0, 12).map(([id, label]) => (
                      <li key={id}>
                        <button type="button"
                          onMouseDown={(e) => { e.preventDefault(); addArea(id); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-cyan-400/10 focus:bg-cyan-400/10"
                          data-testid={`ai-legal-area-option-${id}`}>
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Campo libre — sólo cuando primary === 'other' en modo manual */}
            {legalAreaMode === "manual" && primaryArea === "other" && (
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  Especifica la materia jurídica
                </label>
                <input
                  type="text"
                  className="field w-full text-sm"
                  placeholder="Ej: Derecho aeronáutico…"
                  value={customLegalArea}
                  onChange={(e) => setCustomLegalArea(e.target.value.slice(0, 80))}
                  maxLength={80}
                  data-testid="ai-custom-legal-area"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  {customLegalArea.length}/80 caracteres
                </div>
              </div>
            )}
          </div>
        </aside>

        <div className="lg:col-span-3 panel p-5 space-y-4">
          <div>
            <label className="label-eyebrow">Antecedentes del caso</label>
            <div
              className="mt-2 text-[11px] text-cyan-200/90 bg-cyan-400/5 border border-cyan-400/20 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
              data-testid="ai-quota-inline-label"
            >
              <Info size={11}/> 30 consultas mensuales incluidas + Pack extra de 20 por {new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(config?.ai_extra_pack_clp || 9990)}
            </div>
            <textarea
              className="field mt-2 w-full text-sm colega-ia-font"
              rows={8}
              placeholder="Describe los antecedentes o el problema jurídico a investigar. Mínimo 10 caracteres."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              readOnly={Boolean(draftingClarification)}
              aria-readonly={Boolean(draftingClarification)}
              maxLength={8000}
              data-testid="ai-context-input"/>
            <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
              <Info size={11}/> {context.length}/8.000 caracteres
            </div>
          </div>

          {draftingClarification && (
            <section
              className="rounded-md border border-amber-300/35 bg-amber-300/[0.06] p-4"
              data-testid="ai-drafting-clarification"
            >
              <div className="label-eyebrow text-amber-200">Aclaración necesaria</div>
              <p className="mt-2 text-sm leading-relaxed text-amber-50" data-testid="ai-drafting-clarification-prompt">
                {draftingClarification.prompt}
              </p>
              {draftingClarification.options.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-drafting-clarification-options">
                  {draftingClarification.options.map((option, index) => {
                    const selected = draftingClarificationResponse === option.label
                      && (!option.kind || draftingSelectedBranch === option.value);
                    return (
                      <button
                        key={`${option.id || index}-${option.value}`}
                        type="button"
                        onClick={() => {
                          setDraftingClarificationResponse(option.label);
                          if (option.kind === "branch") setDraftingSelectedBranch(option.value);
                        }}
                        disabled={loading}
                        aria-pressed={selected}
                        className={`px-3 py-2 rounded-md border text-left text-xs transition-[background-color,border-color,transform] hover:-translate-y-0.5 ${selected
                          ? "border-amber-200 bg-amber-200/20 text-amber-50"
                          : "border-white/15 bg-black/20 text-white/85 hover:border-amber-200/50"
                        }`}
                        data-testid={`ai-drafting-clarification-option-${option.id || index}`}
                      >
                        <span className="font-semibold text-amber-200">({option.id || String.fromCharCode(97 + index)})</span>{" "}
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
              <label className="mt-4 block text-xs font-medium text-amber-100" htmlFor="ai-drafting-clarification-response">
                Tu respuesta
              </label>
              <textarea
                id="ai-drafting-clarification-response"
                className="field mt-2 w-full text-sm colega-ia-font"
                rows={3}
                value={draftingClarificationResponse}
                onChange={(e) => {
                  setDraftingClarificationResponse(e.target.value.slice(0, 2000));
                  if (draftingClarification.kind === "branch") setDraftingSelectedBranch("");
                }}
                disabled={loading}
                maxLength={2000}
                placeholder="Selecciona una alternativa o responde con tus propias palabras…"
                data-testid="ai-drafting-clarification-response"
              />
              <div className="mt-1 text-[11px] text-amber-100/60" data-testid="ai-drafting-clarification-counter">
                {draftingClarificationResponse.length}/2.000 caracteres
              </div>
            </section>
          )}

          {/* Adjuntar archivos (PDF · Word · TXT · JPG) */}
          <div className="p-3 rounded-md border border-dashed border-cyan-400/30 bg-cyan-400/[0.04]">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="label-eyebrow text-cyan-300 inline-flex items-center gap-1.5">
                <Paperclip size={11}/> Adjuntar archivos para análisis
              </div>
              <span className="text-[10px] text-muted-foreground">PDF · Word · TXT · JPG · máx. 5 archivos, 20 MB c/u</span>
            </div>
            <input
              ref={attachmentInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt,.md,image/jpeg,image/jpg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={async (e) => {
                setAttachErr("");
                const files = Array.from(e.target.files || []);
                const validation = validateAttachmentSelection(attachments.length, files);
                if (validation.error) setAttachErr(validation.error);
                if (!validation.validFiles.length) {
                  e.target.value = "";
                  return;
                }
                setAttachmentsEncoding(true);
                for (const f of validation.validFiles) {
                  try {
                    const attachment = await readFileAsAttachment(f);
                    setAttachments((prev) => [...prev, attachment]);
                  } catch (err) {
                    setAttachErr(err.message || `No se pudo leer "${f.name}".`);
                  }
                }
                setAttachmentsEncoding(false);
                e.target.value = "";  // permitir re-seleccionar el mismo
              }}
              disabled={Boolean(draftingClarification) || loading}
              className="mt-2 block w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-white/10 file:text-foreground hover:file:bg-white/15 cursor-pointer"
              data-testid="ai-attach-input"
            />
            {attachErr && <div className="mt-2 text-xs text-red-300" data-testid="ai-attach-err">{attachErr}</div>}
            {attachmentsEncoding && (
              <div className="mt-2 text-xs text-cyan-200" data-testid="ai-attach-encoding">
                Codificando archivos…
              </div>
            )}
            {attachments.length > 0 && (
              <ul className="mt-3 space-y-1" data-testid="ai-attach-list">
                {attachments.map((a, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-2 text-[11px] bg-black/20 border border-white/5 rounded px-2 py-1">
                    <span className="truncate inline-flex items-center gap-1.5">
                      <FileText size={11} className="text-cyan-300 shrink-0"/>
                      <span className="truncate">{a.name}</span>
                      <span className="text-muted-foreground">· {a.sizeKB} KB</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                      disabled={Boolean(draftingClarification) || loading}
                      className="text-red-300 hover:text-red-200 shrink-0"
                      data-testid={`ai-attach-remove-${idx}`}
                    >Quitar</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button type="submit" className="btn-primary w-full disabled:opacity-60" disabled={loading || attachmentsEncoding || (draftingClarification ? draftingClarificationResponse.trim().length < 2 : !canSubmitAssist(context, attachments))}
            data-testid="ai-submit-btn">
            {loading
              ? <><Loader2 size={14} className="animate-spin mr-1.5"/> Colega IA está redactando…</>
              : <><Send size={14} className="mr-1.5"/> {draftingClarification ? "Continuar redacción" : "Consultar a Colega IA"}</>}
          </button>

          {loading && (status || heartbeats > 0) && (
            <div className="mt-2 text-xs text-cyan-300/90 inline-flex items-center gap-1.5" data-testid="ai-stream-status">
              <Loader2 size={12} className="animate-spin"/>
              <span>{status || "Redactando con Colega IA..."}</span>
              {heartbeats > 0 && (
                <span className="text-muted-foreground">· {heartbeats}s</span>
              )}
            </div>
          )}

          {error && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="ai-error">{error}</div>}

          {partialEvidence?.recoverable && (
            <div className="mt-2 rounded-md border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-100" data-testid="ai-partial-warning">
              <div className="inline-flex items-center gap-1.5 font-medium">
                <AlertTriangle size={13} className="text-amber-300"/>
                Síntesis incompleta
              </div>
              <p className="mt-1 text-amber-100/80">
                La búsqueda y verificación terminaron, pero la síntesis no alcanzó a completarse. Se muestran sólo evidencias aprobadas.
              </p>
            </div>
          )}

          {(output || partialEvidence?.normativa?.length || partialEvidence?.jurisprudencia?.length) && (
            <div className="mt-2" data-testid="ai-output">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="label-eyebrow text-emerald-300 inline-flex items-center gap-1.5">
                  <BookMarked size={12}/> Respuesta de Colega IA
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button type="button" onClick={copyOutput} className="btn-ghost text-xs py-1.5" data-testid="ai-copy-btn">
                    {copied ? <><Check size={12} className="mr-1"/> Copiado</> : <><Copy size={12} className="mr-1"/> Copiar</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => exportFile("pdf")}
                    className="btn-ghost text-xs py-1.5 border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
                    disabled={exporting !== null}
                    data-testid="ai-export-pdf-btn"
                  >
                    {exporting === "pdf" ? (
                      <><Loader2 size={12} className="animate-spin mr-1"/> Generando…</>
                    ) : (
                      <><FileDown size={12} className="mr-1"/> Exportar PDF</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => exportFile("docx")}
                    className="btn-ghost text-xs py-1.5 border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
                    disabled={exporting !== null}
                    data-testid="ai-export-docx-btn"
                  >
                    {exporting === "docx" ? (
                      <><Loader2 size={12} className="animate-spin mr-1"/> Generando…</>
                    ) : (
                      <><Download size={12} className="mr-1"/> Exportar Word</>
                    )}
                  </button>
                </div>
              </div>
              {output && <div
                className="colega-ia-output panel p-5 border-emerald-400/20 bg-emerald-400/[0.02]"
                data-testid="ai-output-rendered"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {output}
                </ReactMarkdown>
              </div>}
              {partialEvidence?.normativa?.length > 0 && (
                <div className="mt-3 panel p-4 border-cyan-400/20" data-testid="ai-partial-normativa">
                  <div className="label-eyebrow text-cyan-300">Normativa aprobada</div>
                  <div className="mt-2 space-y-3">
                    {partialEvidence.normativa.map((source, index) => (
                      <div key={`${source.url || "norma"}-${index}`} className="text-sm">
                        <div className="font-medium">{source.title || source.articulo || source.numero || "Fuente normativa"}</div>
                        {source.texto && <div className="mt-1 whitespace-pre-wrap text-muted-foreground">{source.texto}</div>}
                        {source.url && <a className="mt-1 inline-block text-xs text-cyan-300 underline" href={source.url} target="_blank" rel="noreferrer">Fuente oficial</a>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {partialEvidence?.jurisprudencia?.length > 0 && (
                <div className="mt-3 panel p-4 border-emerald-400/20" data-testid="ai-partial-jurisprudencia">
                  <div className="label-eyebrow text-emerald-300">Jurisprudencia verificada</div>
                  <div className="mt-2 space-y-3">
                    {partialEvidence.jurisprudencia.map((decision, index) => (
                      <div key={`${decision.url || decision.rol || "fallo"}-${index}`} className="text-sm">
                        <div className="font-medium">{decision.rol || "Decisión verificada"}</div>
                        {decision.evidence_excerpt && <div className="mt-1 whitespace-pre-wrap text-muted-foreground">{decision.evidence_excerpt}</div>}
                        {decision.url && <a className="mt-1 inline-block text-xs text-emerald-300 underline" href={decision.url} target="_blank" rel="noreferrer">Fuente oficial</a>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-muted-foreground mt-2 inline-flex items-center gap-1">
                <Info size={11}/> Las referencias aparecen como <strong>notas al pie numeradas</strong> al final de la respuesta.
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

function UpgradeScreen() {
  const track = () => api.post("/ai/track", { event: "ai_upgrade_clicked" }).catch(() => {});
  return (
    <div className="container-app py-16 max-w-4xl colega-ia-font" data-testid="ai-upgrade-screen">
      <div className="panel p-10 text-center border-amber-400/30 bg-amber-400/5">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400/30 to-cyan-400/20 border border-amber-400/40 flex items-center justify-center">
          <Sparkles size={32} className="text-amber-300"/>
        </div>
        <div className="label-eyebrow text-amber-300 mt-4">Beneficio Pionero Fundador</div>
        <h1 className="serif text-4xl sm:text-5xl mt-3">Colega IA<br/>solo para miembros</h1>
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
          Asistente jurídico experto en derecho chileno vigente, con citas académicas de sus fuentes y notas al pie. Un beneficio
          incluido en el <strong className="text-foreground">Plan Fundador</strong> — cap estricto de 50 cupos con precio congelado de por vida.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left">
          {[
            { title: "Experto en derecho chileno", desc: "Prioriza normativa vigente: Constitución, códigos, leyes, auto-acordados y jurisprudencia de Corte Suprema y Cortes de Apelaciones." },
            { title: "Citas estilo APA", desc: "Cada afirmación jurídica va referenciada con estilo académico y las fuentes se listan como notas al pie numeradas." },
            { title: "Investigación jurídica verificada", desc: "Localiza y contrasta jurisprudencia oficial (pjud.cl, BCN) antes de citarla, con estado de verificación en cada fuente." },
            { title: "Materias jurídicas configurables", desc: "Detección automática o selección manual de materia principal, relacionadas y campo libre para materias no listadas." },
          ].map((b, i) => (
            <div key={i} className="p-4 rounded-md border border-white/10 bg-white/[0.02]">
              <div className="text-sm font-medium text-cyan-100">{b.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{b.desc}</div>
            </div>
          ))}
        </div>

        <Link to="/planes" onClick={track} className="btn-primary mt-10 inline-flex text-base py-3 px-8" data-testid="ai-upgrade-cta">
          <Sparkles size={16} className="mr-2"/> Ver planes y activar mi membresía
        </Link>
        <div className="text-xs text-muted-foreground mt-4">
          El Founder Plan cuesta $239.900/año (equivalente a $19.990/mes). Precio congelado de por vida.
        </div>
      </div>
    </div>
  );
}
